import type {
  DosenSubRole,
  EventType,
  HaltReason,
  RequestType,
  Role,
  SkripsiStage,
  SubRoleScope,
} from './types';

export const ROLE_META: Record<
  Role,
  { label: string; short: string; description: string; color: string }
> = {
  mahasiswa: {
    label: 'Mahasiswa',
    short: 'Mhs',
    description: 'Mengelola skripsi, bimbingan, dan jadwal seminar/ujian.',
    color: 'var(--color-role-mahasiswa)',
  },
  dosen: {
    label: 'Dosen',
    short: 'Dsn',
    description: 'Membimbing, menguji, dan menyetujui progres skripsi.',
    color: 'var(--color-role-dosen)',
  },
  staf_jurusan: {
    label: 'Staf Jurusan',
    short: 'Jur',
    description: 'Memverifikasi berkas, pendaftaran, dan jadwal program studi.',
    color: 'var(--color-role-staf-jurusan)',
  },
  staf_fakultas: {
    label: 'Staf Fakultas',
    short: 'Fak',
    description: 'Mengawasi, melaporkan, dan menetapkan kebijakan fakultas.',
    color: 'var(--color-role-staf-fakultas)',
  },
  admin: {
    label: 'Administrator',
    short: 'Adm',
    description: 'Mengelola pengguna, peran, dan konfigurasi sistem.',
    color: 'var(--color-role-admin)',
  },
};

export const DOSE_ROLE_META: Record<
  DosenSubRole,
  { label: string; scopes: SubRoleScope[]; description: string }
> = {
  'pembimbing-akademik': {
    label: 'Dosen Pembimbing Akademik',
    scopes: ['mahasiswa'],
    description: 'Mendampingi mahasiswa bimbingan akademik (PA).',
  },
  'pembimbing-1': {
    label: 'Pembimbing 1',
    scopes: ['skripsi'],
    description: 'Pembimbing utama skripsi.',
  },
  'pembimbing-2': {
    label: 'Pembimbing 2',
    scopes: ['skripsi'],
    description: 'Pembimbing pendamping skripsi.',
  },
  'penguji-1': {
    label: 'Penguji 1',
    scopes: ['seminar', 'ujian'],
    description: 'Penguji seminar proposal dan ujian skripsi.',
  },
  'penguji-2': {
    label: 'Penguji 2',
    scopes: ['seminar', 'ujian'],
    description: 'Penguji seminar proposal dan ujian skripsi.',
  },
  moderator: {
    label: 'Moderator',
    scopes: ['seminar', 'ujian'],
    description: 'Memandu jalannya seminar dan ujian.',
  },
};

/** Ordered lifecycle of a skripsi — drives the progress timeline everywhere. */
export const SKRIPSI_STAGES: SkripsiStage[] = [
  'penetapan_pembimbing',
  'bimbingan_proposal',
  'seminar_proposal',
  'revisi_proposal',
  'penelitian',
  'bimbingan_skripsi',
  'ujian_skripsi',
  'revisi_skripsi',
  'selesai',
];

export const STAGE_META: Record<SkripsiStage, { label: string; short: string }> = {
  penetapan_pembimbing: { label: 'Penetapan Pembimbing', short: 'Pembimbing' },
  bimbingan_proposal: { label: 'Bimbingan Proposal', short: 'Sempro' },
  seminar_proposal: { label: 'Seminar Proposal', short: 'Sempro' },
  revisi_proposal: { label: 'Revisi Proposal', short: 'Revisi' },
  penelitian: { label: 'Penelitian', short: 'Riset' },
  bimbingan_skripsi: { label: 'Bimbingan Skripsi', short: 'Bimbingan' },
  ujian_skripsi: { label: 'Ujian Skripsi', short: 'Sidang' },
  revisi_skripsi: { label: 'Revisi Skripsi', short: 'Revisi' },
  selesai: { label: 'Selesai', short: 'Selesai' },
};

export function stageIndex(stage: SkripsiStage): number {
  return SKRIPSI_STAGES.indexOf(stage);
}

/**
 * Pure stage-order rule: is `target` reachable from `cur` as a legitimate
 * sequential forward move (no override needed)? True for exactly-one-step
 * advances, plus the passing-sidang completion `ujian_skripsi → selesai`
 * (skips the revision stop). Any backward move or forward skip returns false.
 */
export function isSequentialMove(cur: SkripsiStage, target: SkripsiStage): boolean {
  return (
    target === SKRIPSI_STAGES[stageIndex(cur) + 1] ||
    (cur === 'ujian_skripsi' && target === 'selesai')
  );
}

/** Why a skripsi was halted — drives the reason selector + history display. */
export const HALT_REASON_META: Record<HaltReason, { label: string }> = {
  ditolak_sempro: { label: 'Tidak lulus seminar proposal' },
  ditolak_sidang: { label: 'Tidak lulus ujian skripsi' },
  tidak_lanjut_bimbingan: { label: 'Tidak melanjutkan bimbingan' },
  anulir: { label: 'Anulir' },
  lainnya: { label: 'Lainnya (keputusan dosen/staf)' },
};

export const EVENT_TYPE_META: Record<EventType, { label: string; short: string }> = {
  seminar_proposal: { label: 'Seminar Proposal', short: 'Sempro' },
  ujian_skripsi: { label: 'Ujian Skripsi', short: 'Sidang' },
};

/** Human labels for request types (registration flows). */
export const REQUEST_TITLE: Record<RequestType, string> = {
  seminar_proposal: 'Pendaftaran seminar',
  penelitian: 'Pendaftaran penelitian',
  ujian_skripsi: 'Pendaftaran ujian',
};

/** Team roles that map a dosen to a specific skripsi. */
export const SKRIPSI_TEAM_ROLES = [
  'pembimbing-1',
  'pembimbing-2',
  'penguji-1',
  'penguji-2',
  'moderator',
] as const;

export const EMAIL_DEMO_DOMAINS = ['student.univ.ac.id', 'lecturer.univ.ac.id', 'staf.univ.ac.id'];

/** Jenis kelamin options for biodata (dosen & mahasiswa). */
export const JK_OPTIONS = ['Laki-laki', 'Perempuan'] as const;

/** Jabatan fungsional akademik dosen. */
export const JABATAN_FUNGSIONAL_OPTIONS = [
  'Tenaga Pengajar',
  'Asisten Ahli',
  'Lektor',
  'Lektor Kepala',
  'Profesor',
] as const;

/** Golongan ruang PNS dosen. */
export const GOLONGAN_OPTIONS = [
  'III/a',
  'III/b',
  'III/c',
  'III/d',
  'IV/a',
  'IV/b',
  'IV/c',
  'IV/d',
  'IV/e',
] as const;

/** Fakultas default untuk akun/mahasiswa baru (sementara seluruh prodi di bawah FKIP). */
export const DEFAULT_FAKULTAS = 'FKIP';
